import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { OrcaFarmInfo } from "@stepfinance/farms/src/lib/info/orca/types";
import { OrcaWhirlpoolPositionsFetcher } from "@stepfinance/liquidity";
import type { TokenAccount } from "@stepfinance/tokens";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useMarkets } from "../../../contexts/market";
import { getTokenByMint } from "../../../utils/tokenInfo";

export type AquafarmUserTokenAccount = OrcaFarmInfo & {
  tokenAccount: TokenAccount;
};

const handleOrcaWhirlpoolsError = (error) => {
  console.error("Orca Whirlpools Error:", error);
  captureException(error);
};

export function useOrcaWhirlpoolsQuery() {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();

  const whirlpoolPositionsFetcher = useMemo(() => {
    if (connected && marketsInitialised && publicKey) {
      return new OrcaWhirlpoolPositionsFetcher({ connection, publicKey, getTokenByMint });
    }
  }, [connected, connection, marketsInitialised, publicKey]);

  return useQuery(
    ["orca-whirlpools", publicKey?.toBase58()],
    async () => {
      if (whirlpoolPositionsFetcher) {
        try {
          return await whirlpoolPositionsFetcher.fetchPositions(priceInUSD);
        } catch (error) {
          handleOrcaWhirlpoolsError(error);
        }
      }
      return [];
    },
    { retry: false, enabled: whirlpoolPositionsFetcher !== undefined },
  );
}
